/* eslint-disable object-shorthand */
import {
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	Icon,
	IconButton,
	Tooltip
} from "@mui/material";
import MDBox from "components/Basics/MDBox";
import MDButton from "components/Basics/MDButton";
import MDTypography from "components/Basics/MDTypography";
import { t } from "i18next";
import { useEffect, useState } from "react";
import ReviewType from "./Types/ReviewType";
import TicketType from "./Types/TicketType";
import { TYPE, ANSWERS_FORMAT, STEP_TYPES } from "../constants";
import AdvancedInput2 from "components/Custom/AdvancedInput2";
import ThemeType from "./Types/ThemeType";

/**
 * Language input component
 * @param {*} param0
 * @returns
 */
const LanguageInput = ({
	//
	displayAttachments = true, // Display attachments part or not
	displaySubject = true, // Display subject part or not
	displayGeneration = true, // Display AI generation button or not
	//
	code,
	language,
	languages,
	alternative,
	alternativeIndex,
	channelType,
	dictionary,
	rightMenuPannel,
	setRightMenuPannel,
	onChangePath,
	exitDialogOpen,
	setExitDialogOpen,
	addNewAttachment,
	onHtmlEditorSave,
	editMode,
	answerItemCode,
	typologySelected,
	stepTypeSelected
}) => {
	// Get the language alternative content object (text, attachments, subject, etc...)
	const languageAlternativeContent = alternative?.[language?.code];
	const value = languageAlternativeContent?.text || "";

	const [attachments, setAttachments] = useState(languageAlternativeContent?.attachments || []);

	/**
	 * Depending on the channel type, return the right editor component
	 * @returns
	 */

	useEffect(() => {
		if (channelType === TYPE.steps && stepTypeSelected === STEP_TYPES.pitch) {
			onChangePath(ANSWERS_FORMAT.menu, undefined);
		}
	}, [stepTypeSelected]);

	const getEditorComponent = () => {
		switch (channelType) {
			case TYPE.themeCategory:
			case TYPE.themeQuestion: {
				return (
					<ThemeType
						alternativeIndex={alternativeIndex}
						value={languageAlternativeContent}
						dictionary={dictionary}
						setRightMenuPannel={setRightMenuPannel}
						language={language}
						languages={languages}
						onChangePath={onChangePath}
						channelType={channelType}
					/>
				);
			}

			case TYPE.conversational:
			case TYPE.review:
				// Review type editor
				return (
					<ReviewType
						alternativeIndex={alternativeIndex}
						value={value}
						dictionary={dictionary}
						setRightMenuPannel={setRightMenuPannel}
						language={language}
						languages={languages}
						onChangePath={onChangePath}
						contact={languageAlternativeContent?.contact}
						location={languageAlternativeContent?.location}
						menu={languageAlternativeContent?.menu}
						link={languageAlternativeContent?.link}
						attachments={languageAlternativeContent?.attachments}
						locationRequest={languageAlternativeContent?.locationRequest}
						isEditMode={Boolean(answerItemCode)}
						typologyType={typologySelected}
						channelType={channelType}
						code={code}
					/>
				);
			case TYPE.news:
			case TYPE.ticket:
				// Ticket type editor
				return (
					<TicketType
						displayAttachments={displayAttachments}
						displaySubject={displaySubject}
						//
						code={code}
						value={value}
						dictionary={dictionary}
						alternativeIndex={alternativeIndex}
						languageAlternativeContent={languageAlternativeContent}
						attachments={attachments}
						setAttachments={setAttachments}
						onChangePath={onChangePath}
						language={language}
						addNewAttachment={addNewAttachment}
						onHtmlEditorSave={onHtmlEditorSave}
						channelType={channelType}
						alternative={alternative}
					/>
				);
			case TYPE.steps:
				if (stepTypeSelected === "pitch") {
					return (
						<TicketType
							displayAttachments={displayAttachments}
							displaySubject={displaySubject}
							//
							code={code}
							value={value}
							dictionary={dictionary}
							alternativeIndex={alternativeIndex}
							languageAlternativeContent={languageAlternativeContent}
							attachments={attachments}
							setAttachments={setAttachments}
							onChangePath={onChangePath}
							language={language}
							addNewAttachment={addNewAttachment}
							onHtmlEditorSave={onHtmlEditorSave}
							channelType={channelType}
							alternative={alternative}
						/>
					);
				} else if (stepTypeSelected === "solution") {
					return (
						<ReviewType
							alternativeIndex={alternativeIndex}
							value={value}
							dictionary={dictionary}
							setRightMenuPannel={setRightMenuPannel}
							language={language}
							languages={languages}
							onChangePath={onChangePath}
							contact={languageAlternativeContent?.contact}
							location={languageAlternativeContent?.location}
							menu={languageAlternativeContent?.menu}
							link={languageAlternativeContent?.link}
							attachments={languageAlternativeContent?.attachments}
							locationRequest={languageAlternativeContent?.locationRequest}
							isEditMode={Boolean(answerItemCode)}
							typologyType="menu"
							channelType={channelType}
							code={code}
							stepTypeSelected={stepTypeSelected}
						/>
					);
				} else {
					return null;
				}
			default:
				// Default editor (none)
				return null;
		}
	};

	/**
	 * Get the AI generation icon color
	 * @returns string
	 */
	const getIconAIGenerationColor = () => {
		if (
			rightMenuPannel.open &&
			rightMenuPannel.alternative === alternativeIndex &&
			rightMenuPannel.language === language.code
		) {
			return "info";
		}
		return "default";
	};

	/**
	 * Reload the attachments list when the alternative index change
	 */
	useEffect(() => {
		setAttachments(languageAlternativeContent?.attachments || []);
	}, [alternativeIndex]);

	return (
		<MDBox
			sx={{ mb: 1 }}
			display="flex"
			bgColor="white"
			p={1}
			borderRadius="md"
			style={{
				border: "1px solid rgb(232, 232, 238)"
			}}
			flexDirection="column"
		>
			{/* Top bar */}
			<MDBox display="flex" alignItems="center" justifyContent="space-between">
				<MDBox display="flex" alignItems="center">
					{/* Logo */}
					{language.logoURL && (
						<img
							src={language.logoURL}
							alt={language.code}
							style={{
								width: "20px",
								height: "20px",
								borderRadius: "50%",
								marginRight: "10px"
							}}
						/>
					)}
					{/* Name */}
					<MDTypography variant="body2">{language.name}</MDTypography>
				</MDBox>
				{/* AI Generation */}
				{displayGeneration && (
					<MDBox>
						<Tooltip placement="top" title={t("COMPONENT.ANSWERTYPE.generateAnAnswer")}>
							<IconButton
								color={getIconAIGenerationColor()}
								size="small"
								onClick={() => {
									setRightMenuPannel({
										open: true,
										alternative: alternativeIndex,
										language: language.code,
										languages: languages,
										extra: {
											categoryName: "football",
											themeName: "sports",
											themeDescription: "thème sur les sports"
										},
										channelType
									});
								}}
							>
								<Icon>emoji_objects</Icon>
							</IconButton>
						</Tooltip>
					</MDBox>
				)}
			</MDBox>

			{/* Editor, depending on answer type */}
			<MDBox sx={{ mt: 1 }}>{getEditorComponent()}</MDBox>

			{/* Exit dialog confirmation */}
			<Dialog
				open={exitDialogOpen}
				onClose={() => setExitDialogOpen(false)}
				fullWidth
				maxWidth="md"
			>
				<DialogTitle>{t("EDITOR.leaveEditor")}</DialogTitle>
				<DialogContent>{t("EDITOR.leaveEditorMessage")}</DialogContent>
				<DialogActions>
					<MDButton variant="outlined" color="info" onClick={() => setExitDialogOpen(false)}>
						{t("SETTINGS.cancel")}
					</MDButton>
					<MDButton
						variant="contained"
						color="error"
						onClick={() => {
							setExitDialogOpen(false);
						}}
					>
						{t("SETTINGS.confirm")}
					</MDButton>
				</DialogActions>
			</Dialog>
		</MDBox>
	);
};

/**
 * Alternative component
 * @param {*} param0
 * @returns JSX
 */
export default function Alternative({
	//
	displayAttachments = true, // Display attachments part or not
	displaySubject = true, // Display subject part or not
	displayGeneration = true, // Display AI generation button or not
	//
	code,
	alternative,
	languages,
	dictionary,
	channelType,
	alternativeIndex,
	onChangePath,
	rightMenuPannel,
	setRightMenuPannel,
	addNewAttachment,
	answerItemCode,
	typologySelected,
	stepTypeSelected
}) {
	const [exitDialogOpen, setExitDialogOpen] = useState(false);

	/**
	 * Handle the editor save
	 * @param {String} html The HTML content
	 * @param {Boolean} editMode If the editor is in edit mode
	 * @param {String} language Language
	 *
	 *
	 */
	const htmlEditorSave = (html, editMode, language) => {
		onChangePath(alternativeIndex, language, "text", html);
	};

	/**
	 * Main component
	 */
	return (
		<MDBox
			display="flex"
			flexDirection="row"
			style={{
				height: "100%",
				backgroundColor: "rgb(251, 251, 252)"
			}}
		>
			<MDBox
				flex="1"
				p={1}
				style={{
					position: "relative",
					width: "100%"
				}}
			>
				{languages.map((LANGUAGE, index) => {
					return (
						<LanguageInput
							displayAttachments={displayAttachments}
							displaySubject={displaySubject}
							displayGeneration={displayGeneration}
							code={code}
							key={index}
							language={LANGUAGE}
							dictionary={dictionary}
							channelType={channelType}
							alternative={alternative}
							alternativeIndex={alternativeIndex}
							rightMenuPannel={rightMenuPannel}
							setRightMenuPannel={setRightMenuPannel}
							languages={languages}
							onChangePath={(path, value) => {
								onChangePath(alternativeIndex, LANGUAGE.code, path, value);
							}}
							exitDialogOpen={exitDialogOpen}
							setExitDialogOpen={setExitDialogOpen}
							addNewAttachment={addNewAttachment}
							onHtmlEditorSave={htmlEditorSave}
							answerItemCode={answerItemCode}
							typologySelected={typologySelected}
							stepTypeSelected={stepTypeSelected}
						/>
					);
				})}
			</MDBox>
		</MDBox>
	);
}
